'use strict'

import L from 'leaflet'

let ZoomControl = L.Control.extend({
    options: {
        position: 'topright'
    },
    onAdd: function () {
        let container = L.DomUtil.create('a', 'zoom-control');

        let block = $(container);

        block.attr('title', 'To markers');

        block.html("<i class='hypericons hypericon-target-lock'></i>");

        block.on('click', function () {
            const map = block.parentsUntil('.map').last().parent()

            if (!map.is('.map')) {
                return;
            }

            const mapId = map.attr('id')

            const mapBuilder = MapBuilder.getMapInstance(mapId)

            mapBuilder.zoomToMarkers()
        })


        return container;
    }
})

class MapBuilder {
    /**
     * MapBuilder config
     *
     * @type {Object}
     * @property {string} mapId
     * @property {Object} zoom
     * @property {number} zoom.start
     * @property {number} zoom.min
     * @property {number} zoom.max
     * @property {Array} markers
     * @property {boolean} markersLine
     * @property {Array} centerPoint
     * @property {string} mapper
     * @property {string} attribution
     */
    #config = {
        mapId: "",
        zoom: {
            start: 3,
            min: 3,
            max: 15,
        },
        markers: [
            {
                lat: 0,
                long: 0,
            }
        ],
        markersLine: false,
        centerPoint: [0, 0],
        attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors, Imagery © Mapbox.com',
        mapper: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    }

    #partialsConfigs = {
        /**
         * Options for Map Object
         *
         * @link https://leafletjs.com/reference.html#map-option
         */
        map: {
            worldCopyJump: true,
        },
        /**
         * Options for Polyline Object
         *
         * @link https://leafletjs.com/reference.html#polyline
         */
        polyline: {
            color: "#72D3FF",
            interactive: false
        },
        /**
         * Options for Map fitBounds option Object
         *
         * @link https://mourner.github.io/Leaflet/reference.html#map-fitboundsoptions
         */
        fitBounds: {
            animate: true
        }
    }

    /**
     * Leaflet instance
     *
     * @type {L}
     * @link https://leafletjs.com/index.html
     */
    #leaf = L

    /**
     * Map instance
     *
     * @type {Map|null}
     * @link https://leafletjs.com/reference.html#map
     */
    #map = null;

    /**
     * Map markers
     *
     * @link https://leafletjs.com/reference.html#marker
     */
    #markers = []

    /**
     * Map polyline
     *
     * @type {Polyline|null}
     * @link https://leafletjs.com/reference.html#polyline
     */
    #polyline = null

    /**
     * Markers line coordinates
     *
     * @type {Array}
     */
    #markersLineCoordinates = []

    #mapper = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"

    /**
     * Constructs a new instance of the class with the given configuration.
     *
     * @param {Object} config - The configuration object for the instance.
     */
    constructor(config = {}) {
        this.#config = config
        this.#mapper = this.#config.mapper ?? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"

        this.#init()
    }

    /**
     * Retrieves the map instance associated with the given map ID.
     *
     * @param {string} mapId - The ID of the map instance to retrieve. Defaults to an empty string.
     * @return {MapBuilder|null} The map instance associated with the given map ID, or null if no such instance exists.
     */
    static getMapInstance(mapId = '') {
        return 'mapBuilderMaps' in window && mapId in window.mapBuilderMaps ? window.mapBuilderMaps[mapId] : null
    }

    /**
     * Zooms to markers on the map.
     *
     * @return {MapBuilder} - The current instance of the `MapBuilder` object.
     */
    zoomToMarkers() {
        if (this.#config.markersLine) {
            this.#fitBounds(this.#polyline.getBounds())

            return this;
        }

        this.#map.setView(this.#markers[0].getLatLng(), this.#config.zoom.start)


        return this;
    }

    /**
     * Changes the markers on the map and redraws it if necessary.
     *
     * @param {Array} markers - The new markers to be displayed on the map.
     * @param {boolean} drawLine - Whether to draw a line connecting the markers.
     */
    changeMarkers(markers = [], drawLine = false) {
        if(markers.length == 0){
            markers = [{
                lat: 0,
                long: 0,
            }]
        }

        $.each(this.#markers, (index, marker) => {
            marker.removeFrom(this.#map)
        })

        if (this.#config.markersLine) {
            this.#polyline.remove()
        }

        this.#config.markers = markers
        this.#config.markersLine = drawLine
        this.#polyline = null;

        this.#setMarkers()
        this.#drawMap()


        return this;
    }

    /**
     * Resizes the map.
     *
     * `Attention`: If map block was inside hidden block (block that had `display: none`), map will not be rendered correctly.
     *
     * @return {Object} - The current instance of the MapBuilder object.
     */
    resizeMap() {
        this.#map._onResize()


        return this;
    }

    /**
     * Fits the map bounds to the specified bounds with the given options.
     *
     * @param {LatLngBounds|array} bounds - The bounds to fit the map to.
     * @param {Object} [options={}] - The options for fitting the bounds.
     * @return {void}
     */
    #fitBounds(bounds, options = {}) {
        this.#map.fitBounds(bounds, { ...this.#partialsConfigs.fitBounds, ...options })
    }

    /**
     * Draws the map with specified configurations and markers if enabled.
     */
    #drawMap() {
        this.#leaf
            .tileLayer(this.#mapper, {
                maxZoom: parseInt(this.#config.zoom.max),
                minZoom: parseInt(this.#config.zoom.min),
                attribution: this.#config.attribution ?? "",
            }).addTo(this.#map)

        if (this.#config.markersLine) {
            this.#drawPolyline()

            this.#fitBounds(this.#polyline.getBounds())
        }
    }

    /**
     * Draws a polyline on the map using the provided coordinates and configuration.
     *
     * @return {void} This function does not return anything.
     */
    #drawPolyline() {
        this.#polyline = this.#leaf.polyline(this.#markersLineCoordinates, this.#partialsConfigs.polyline).addTo(this.#map)
    }

    /**
     * Initializes the map with the specified configuration.
     */
    #init() {
        if (this.#config.mapId.length == 0) {
            return
        }


        this.#initMap()
        this.#setMarkers()
        this.#drawMap()
        this.#setMapInstance()
    }

    /**
     * Initializes the map.
     */
    #initMap() {
        this.#map = this.#leaf.map(this.#config.mapId, this.#partialsConfigs.map).setView(
            [
                this.#config.centerPoint[0],
                this.#config.centerPoint[1]
            ],
            this.#config.zoom.start
        )

        this.#map.addControl(new ZoomControl())
    }

    /**
     * Sets markers on the map based on the configuration.
     *
     * This function iterates over the markers defined in the configuration and creates a marker on the map for each one.
     * If the marker has an icon defined, it is used to create a custom icon for the marker.
     * If the marker has a title defined, it is used to create a popup for the marker.
     * If the markersLine option is enabled in the configuration, the coordinates of each marker are added to the markersLineCoordinates array.
     * The created markers are added to the markers array.
     *
     * @return {void} This function does not return anything.
     */
    #setMarkers() {
        this.#markers = [];
        this.#markersLineCoordinates = [];

        $.each(this.#config.markers, (index, markerData) => {
            let markerDataLeft = { ...markerData };
            let markerDataRight = { ...markerData };

            markerDataLeft.long = markerDataLeft.long < 0 ? markerDataLeft.long - 360 : markerDataLeft.long + 360
            markerDataRight.long = markerDataRight.long > 0 ? markerDataRight.long - 360 : markerDataRight.long + 360

            let marker = this.#createMarker(index, markerData)
            let markerLeft = this.#createMarker(index, markerDataLeft)
            let markerRight = this.#createMarker(index, markerDataRight)

            if (marker != false) {
                this.#addMarkerToMap(marker)
            }

            if (markerLeft != false) {
                this.#addMarkerToMap(markerLeft)
            }

            if (markerRight != false) {
                this.#addMarkerToMap(markerRight)
            }
        })
    }

    /**
     * Adds a marker to the map and updates the list of markers.
     *
     * @param {Marker} marker - The marker to be added to the map.
     * @return {void} This function does not return anything.
     */
    #addMarkerToMap(marker) {
        marker.addTo(this.#map)

        this.#markers.push(marker)
    }

    /**
     * Creates a marker on the map with the given coordinates and icon.
     *
     * @param {number} index - The index of the marker in the list of markers.
     * @param {Array} markerData - The data for the marker. Defaults to an empty array.
     * @return {Marker|boolean} The created `Marker` object if successful, otherwise false.
     */
    #createMarker(index, markerData = []) {
        if (markerData.length == 0) {
            return false;
        }

        let markerCoordinates = this.#calcMarkerCoordinates(markerData.lat, markerData.long);

        let iconSize = markerData.iconSize ? markerData.iconSize : [25, 41];

        /**
         * Creates a custom icon for the marker.
         * @link https://leafletjs.com/reference.html#icon
         */
        let image = this.#leaf.icon({
            iconUrl: markerData.icon ?? '/tools/img/icons/marker-icon.png',
            iconSize: iconSize,
            iconAnchor: [parseFloat(iconSize[0]) / 2, iconSize[1]]
        });

        /**
             * Creates a marker on the map with the given coordinates and icon.
             * @link https://leafletjs.com/reference.html#marker
             */
        let marker = this.#leaf.marker(markerCoordinates, {
            icon: image,
        });

        let markerTitle = 'title' in markerData && markerData.title.trim().length > 0
            ? markerData.title
            : `${markerCoordinates[0].toFixed(4)}, ${markerCoordinates[1].toFixed(4)}`


        this.#setMarkerTooltip(marker, markerTitle)

        if (this.#config.markersLine && this.#markersLineCoordinates[index] == undefined) {
            this.#markersLineCoordinates.push(markerCoordinates)
        }


        return marker;
    }

    /**
     * Sets a tooltip for a marker on a map.
     *
     * @param {Marker} marker - The marker to attach the tooltip to.
     * @param {string} title - The text to display in the tooltip.
     * @param {Array|null} - The offset of the tooltip relative to the marker's position. If not provided, the tooltip will be positioned below the marker.
     */
    #setMarkerTooltip(marker, title, offset = null) {
        if (offset == null) {
            let icon = marker.options.icon;
            let iconSize = icon.options.iconSize;

            offset = [0, -iconSize[1]]
        }

        marker.bindPopup(title, {
            offset: offset
        });
    }

    /**
     * Calculates marker coordinates based on latitude and longitude.
     *
     * @param {number} lat - The latitude value.
     * @param {number} long - The longitude value.
     * @return {Array} An array containing the marker's latitude and longitude.
     */
    #calcMarkerCoordinates(lat, long) {
        let markerLat = parseFloat(lat)
        let markerLong = parseFloat(long)

        markerLong = Math.abs(markerLong) > 90 && markerLong > 0
            ? (markerLong > 0 ? markerLong - 360 : markerLong + 360)
            : markerLong


        return [markerLat, markerLong]
    }

    /**
     * Sets the current instance of the MapBuilder class as a property of the global window object.
     *
     * @return {void} This function does not return anything.
     */
    #setMapInstance() {
        let mapBuilderMaps = 'mapBuilderMaps' in window ? window.mapBuilderMaps : [];

        mapBuilderMaps[this.#config.mapId] = this

        window.mapBuilderMaps = mapBuilderMaps
    }

    // #redrawPolyline() {
    //     this.#polyline.remove()

    //     this.#drawPolyline()
    // }

    // #createMarkerEvent(marker, index, markerData) {
    //     marker.on('drag', (event) => {
    //         let markerCoordinates = this.#calcMarkerCoordinates(
    //             parseFloat(marker.getLatLng().lat).toFixed(4),
    //             parseFloat(marker.getLatLng().lng).toFixed(4)
    //         );

    //         this.#markersLineCoordinates[index] = markerCoordinates

    //         this.#redrawPolyline()

    //         let markerTitle = 'title' in markerData && markerData.title.trim().length > 0
    //             ? markerData.title
    //             : `${markerCoordinates[0]}, ${markerCoordinates[1]}`

    //         this.#setMarkerTooltip(marker, markerTitle)
    //     })
    // }
}

window.MapBuilder = MapBuilder

typeof mapConfig !== 'undefined' && new MapBuilder(mapConfig)
